import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { countries } from 'countries-list';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { Sheets, ModalHeader, ModalFooter, ModalBody } from '@ingka/modal';
import Button from '@ingka/button';
import useYupValidationResolver from '@/lib/hooks/yup';
import { locationSchema, LocationFormData } from '@/lib/schemas/location';
import ControlledInput from '@/components/forms/controlled-input';
import ControlledDropdown from '@/components/forms/controlled-dropdown';
import ControlledSwitch from '@/components/forms/controlled-switch';
import { useContentTracking } from '@/lib/hooks/tracking';
import ControlledCombo from '@/components/forms/controlled-combo';
import {
  usePrimaryLocationsQuery,
  useSecondaryLocationsQuery,
  type SuperadminLocationsQuery,
} from '@/redux/back-dashboard-api';
import { SUPPORTED_LOCALES } from '@/lib/constants';

type LocationNode =
  SuperadminLocationsQuery['superadminLocations']['nodes'][number];

export type LocationSiderProps = ModalComponentBaseProps & {
  location?: LocationNode;
  onCreate: (data: LocationFormData) => Promise<boolean>;
  onEdit: (data: LocationFormData) => Promise<boolean>;
};

export const NewLocationSider: FC<LocationSiderProps> = ({
  handleDismiss,
  location,
  onCreate,
  onEdit,
  ...rest
}) => {
  // Form
  const resolver = useYupValidationResolver(locationSchema);
  const form = useForm<LocationFormData>({
    mode: 'all',
    resolver,
    criteriaMode: 'all',
    defaultValues: {
      name: location?.name ?? '',
      atkomstClientId: location?.atkomstClientId ?? '',
      primaryCdsLocation:
        location?.cdsLocation?.find(({ primary }) => primary)?.name ?? '',
      cdsLocation:
        location?.cdsLocation
          ?.filter(({ primary }) => !primary)
          .map(({ name }) => name) ?? [],
      country: location?.country ?? '',
      enabled: location?.enabled ?? true,
      mainLanguage: location?.mainLanguage ?? '',
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedCountry = form.watch('country')?.toLowerCase();
  const selectedPrimaryLocation = form.watch('primaryCdsLocation');
  // Hooks
  const { t } = useTranslation();
  const trackContent = useContentTracking();
  const { data: cdsData } = useSecondaryLocationsQuery();
  const { data: atkomstData } = usePrimaryLocationsQuery(
    { country: selectedCountry },
    {
      skip: !selectedCountry,
    }
  );
  // Data
  const isEditMode = !!location;
  const isDisabled = !form.formState.isValid || isSubmitting;
  const languageDropdownItems = SUPPORTED_LOCALES.map((locale) => ({
    value: locale.split('-')[0], // Use only the language code (e.g., "en" from "en-US")
    i18nKey: t(`locales.${locale}`) + ` (${locale})`,
    disabled: false,
  }));
  const countryItems = useMemo(
    () =>
      Object.entries(countries)
        .map(([code, data]) => ({
          value: code.toLowerCase(),
          i18nKey: data.name,
        }))
        .filter(({ value }) => {
          // Only include countries that have at least one CDS location in the data
          if (!cdsData) return false;
          return cdsData.cdsLocations.some((loc) => loc.country === value);
        }),
    [cdsData]
  );
  const cdsLocationItems = useMemo(() => {
    if (!cdsData) return [];

    return cdsData.cdsLocations
      .filter(({ country }) => country === selectedCountry)
      .sort((first, second) => first.name.localeCompare(second.name))
      .map((loc) => ({
        value: loc.name,
        i18nKey: loc.name,
      }));
  }, [cdsData, selectedCountry]);
  const atkomstDataItems = useMemo(() => {
    if (!atkomstData) return [];

    return atkomstData.atkomstClients.map((loc) => ({
      value: loc.id,
      i18nKey: `${loc.id} — ${loc.name}`,
    }));
  }, [atkomstData]);
  // Handlers
  const submitForm = async () => {
    if (isDisabled) return;
    setIsSubmitting(true);

    try {
      const formData = form.getValues();
      const success = isEditMode
        ? await onEdit(formData)
        : await onCreate(formData);

      if (success) handleDismiss();
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    trackContent(isEditMode ? 'edit-location' : 'new-location', {
      type: 'sider',
    });
  }, [trackContent, isEditMode]);

  const { formState, getFieldState } = form;
  const { isDirty: isCountryDirty } = getFieldState('country', formState);
  useEffect(() => {
    if (!isCountryDirty) return; // Only trigger when the country field has been modified by the user
    // Clear Atkomst client ID and CDS location selections when country changes
    form.setValue('atkomstClientId', '');
    form.setValue('primaryCdsLocation', '', { shouldValidate: true });
    form.setValue('cdsLocation', [], { shouldValidate: true });
  }, [selectedCountry, form]);

  useEffect(() => {
    const secondaryLocations = form.getValues('cdsLocation');
    if (!secondaryLocations.includes(selectedPrimaryLocation)) return;

    form.setValue(
      'cdsLocation',
      secondaryLocations.filter((name) => name !== selectedPrimaryLocation),
      { shouldDirty: true, shouldValidate: true }
    );
  }, [selectedPrimaryLocation, form]);

  return (
    <Sheets
      {...rest}
      aria-label={t(isEditMode ? 'label.newLocation' : 'label.editLocation')}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t(isEditMode ? 'label.editLocation' : 'label.newLocation')}
        />
      }
      footer={
        <ModalFooter>
          <Button
            text={t('label.cancel')}
            type="secondary"
            onClick={handleDismiss}
          />

          <Button
            disabled={isDisabled}
            loading={isSubmitting}
            aria-label={t('label.confirm')}
            text={t('label.confirm')}
            type="primary"
            onClick={submitForm}
          />
        </ModalFooter>
      }
    >
      <ModalBody>
        <FormProvider {...form}>
          <div className="flex flex-col gap-4">
            <ControlledInput<LocationFormData>
              name="name"
              label={t('label.name')}
              type="text"
            />

            <ControlledDropdown<LocationFormData>
              name="country"
              label={t('label.country')}
              items={countryItems}
            />

            <ControlledCombo<LocationFormData>
              name="atkomstClientId"
              label={t('label.atkomstLocation')}
              items={atkomstDataItems}
              readOnly={!selectedCountry} // Only allow selection if a country is selected
              showPills={false}
            />

            <ControlledCombo<LocationFormData>
              name="primaryCdsLocation"
              label={t('label.primaryLocation')}
              items={cdsLocationItems}
              showPills={false}
              readOnly={!selectedCountry}
              searchInDropdown
            />

            <ControlledCombo<LocationFormData>
              name="cdsLocation"
              label={t('label.secondaryLocations')}
              items={cdsLocationItems.filter(
                ({ value }) => value !== selectedPrimaryLocation
              )}
              multiple
              showPills
              readOnly={!selectedCountry} // Only allow selection if a country is selected
              searchInDropdown
            />

            <ControlledDropdown<LocationFormData>
              name="mainLanguage"
              label={t('label.language')}
              items={languageDropdownItems}
            />

            <ControlledSwitch<LocationFormData>
              name="enabled"
              label={t('label.enabled')}
            />
          </div>
        </FormProvider>
      </ModalBody>
    </Sheets>
  );
};

export default NewLocationSider;
